import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Trash2, Check, X, Loader2, PencilIcon } from 'lucide-react';
import ProjectHeader from '@/components/header';
import { useAuthState } from '@/hooks/AuthContext';
import { ProjectService } from '@/services/ProjectService';
import { ChevronDown, ChevronRight } from 'lucide-react';

const UserProjects = () => {
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const { user } = useAuthState();
    const userId = user?.id || 'defaultUserId';
    const [projectsData, setProjectsData] = useState({
        collections: [],  // Will hold collection projects
        individualProjects: [] // Will hold standalone projects
    });
    const [expandedCollections, setExpandedCollections] = useState(new Set());
    

    useEffect(() => {
        async function fetchProjects() {
            try {
                setIsLoading(true);
                const userProjects = await ProjectService.getUserProjects(userId);
                
                // Group projects by collections and standalone
                const projectsGrouped = {
                    collections: {},  // Will use an object first for easier grouping
                    individualProjects: []
                };

                // First pass: identify collection projects and get collection data
                for (const project of userProjects) {
                    if (project.collectionId) {
                        if (!projectsGrouped.collections[project.collectionId]) {
                            // Fetch collection data for each unique collectionId
                            const collectionData = await ProjectService.getCollectionData(project.collectionId);
                            projectsGrouped.collections[project.collectionId] = {
                                ...collectionData.data,
                                projects: []
                            };
                        }
                        projectsGrouped.collections[project.collectionId].projects.push(project);
                    } else {
                        projectsGrouped.individualProjects.push(project);
                    }
                }

                // Convert collections object to array and sort everything
                const sortedData = {
                    collections: Object.values(projectsGrouped.collections).sort((a, b) => {
                        // Sort collections by the newest project in each collection
                        const aNewest = Math.max(...a.projects.map(p => parseInt(p.createdAt)));
                        const bNewest = Math.max(...b.projects.map(p => parseInt(p.createdAt)));
                        return bNewest - aNewest;
                    }),
                    individualProjects: projectsGrouped.individualProjects.sort(
                        (a, b) => parseInt(b.createdAt) - parseInt(a.createdAt)
                    )
                };

                setProjectsData(sortedData);
                
                if (userProjects.length === 0) {
                    navigate('/new-project');
                }
            } catch (error) {
                console.error('Error fetching projects:', error);
                navigate('/new-project');
            } finally {
                setIsLoading(false);
            }
        }
        fetchProjects();
    }, [userId, projects, navigate]);
    
    const renderProjects = () => (
        <div className="mb-8">
            {/* Collections Section */}
            {projectsData.collections.length > 0 && (
                <div className="mb-8">
                    {/* <h3 className="text-xl font-semibold mb-4 text-center">Collections:</h3> */}
                    <ul className="space-y-2 max-w-screen-sm mx-auto">
                        {projectsData.collections.map(collection => (
                            <li key={collection.id}>
                                {/* Collection Header - Now clickable and more compact */}
                                <button
                                    onClick={() => toggleCollection(collection.id)}
                                    className="w-full bg-white hover:bg-gray-100 p-2 rounded-lg flex items-center justify-between cursor-pointer border border-gray-200"
                                >
                                    <div className="flex items-center justify-between w-full">
                                        <div className="flex items-center space-x-2">
                                            {expandedCollections.has(collection.id) ? 
                                                <ChevronDown className="h-4 w-4 text-gray-500" /> : 
                                                <ChevronRight className="h-4 w-4 text-gray-500" />
                                            }
                                            <span className="font-medium">{collection.name}</span>
                                            <span className="text-sm text-gray-500">
                                                ({collection.projects.length} {collection.projects.length === 1 ? 'Room' : 'Rooms'})
                                            </span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/collection/${collection.id}/summary`);
                                                }}
                                                className="bg-blue-500 hover:bg-blue-400 text-white p-2"
                                            >
                                                View Summary
                                            </Button>
                                            <Button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteCollection(collection.id);
                                                }}
                                                className="bg-white-500 hover:bg-gray-400 text-red-600 p-2"
                                            >
                                                <Trash2 size={20} />
                                            </Button>
                                        </div>
                                    </div>
                                </button>

                                {/* Projects List - Only shown when collection is expanded */}
                                {expandedCollections.has(collection.id) && (
                                    <ul className="mt-2 space-y-2">
                                        {collection.projects.map(project => (
                                            <li
                                                key={project.id}
                                                className="bg-white rounded-lg shadow p-4 flex items-center justify-between border-l-4 border-blue-500 ml-4"
                                            >
                                                {renderProjectContent(project)}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Individual Projects Section */}
            {projectsData.individualProjects.length > 0 && (
                <div>
                    {/* <h3 className="text-xl font-semibold mb-4 text-center">Individual Projects:</h3> */}
                    <ul className="space-y-4 max-w-screen-sm mx-auto">
                        {projectsData.individualProjects.map(project => (
                            <li
                                key={project.id}
                                className="bg-white rounded-lg shadow p-4 flex items-center justify-between"
                            >
                                {renderProjectContent(project)}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );

    // Extract project content rendering to a separate function
    const renderProjectContent = (project) => (
        <>
            <div className="flex-grow">
                {editingId === project.id ? (
                    <div className="flex items-center">
                        <Input
                            value={editName}
                            onChange={e => setEditName(e.target.value)}
                            className="mr-2"
                        />
                        <Button
                            onClick={() => handleSaveName(project.id)}
                            className="mr-2 bg-green-500 hover:bg-green-400 text-white p-2"
                        >
                            <Check size={20} />
                        </Button>
                        <Button
                            onClick={handleCancelEdit}
                            className="bg-gray-500 hover:bg-gray-400 text-white p-2"
                        >
                            <X size={20} />
                        </Button>
                    </div>
                ) : (
                    <div className="flex flex-col">
                        <div className="flex items-center">
                            <h4 className="font-semibold">{project.name}</h4>
                            <Button
                                onClick={() => handleEditName(project)}
                                className="ml-2 p-0 h-auto bg-transparent hover:bg-transparent"
                                variant="ghost"
                            >
                                <PencilIcon size={16} className="text-gray-500 hover:text-gray-700" />
                            </Button>
                        </div>
                        <p className="text-sm text-gray-500 text-left">
                            Created: {new Date(parseInt(project.createdAt)).toLocaleDateString()}
                        </p>
                    </div>
                )}
            </div>
            <div className="flex items-center">
                <Button
                    // onClick={() => navigate(`/project/${project.id}${project.status === 'submitted' ? '/summary' : ''}`)}
                    onClick={() => {
                        if (project.status === 'submitted' && project.collectionId) {
                            navigate(`/collection/${project.collectionId}/summary`);
                        } else {
                            navigate(`/project/${project.id}${project.status === 'submitted' ? '/summary' : ''}`);
                        }
                    }}
                    className={`mr-2 ${project.status === 'submitted' ? 'bg-green-500 hover:bg-green-400' : 'bg-blue-500 hover:bg-blue-400'} text-white`}
                >
                    {project.status === 'submitted' ? 'Submitted' : 'Continue'}
                </Button>
                <Button
                    onClick={() => handleDeleteProject(project.id)}
                    className="bg-white-500 hover:bg-gray-400 text-red-600 p-2"
                >
                    <Trash2 size={20} />
                </Button>
            </div>
        </>
    );

    const handleStartNewProject = () => {
        navigate('/new-project');
    };

    // const handleDeleteProject = projectId => {
    //     if (window.confirm('Are you sure you want to delete this project?')) {
    //         async function fetchDeleteProject() {
    //             await ProjectService.deleteProject(projectId);
    //             const updatedProjects = projects.filter(project => project.id !== projectId);
    //             setProjects(updatedProjects);
    //         }
    //         fetchDeleteProject();
    //     }
    // };

    const handleDeleteProject = async (projectId) => {
        if (window.confirm('Are you sure you want to delete this project?')) {
            try {
                const project = [...projectsData.collections.flatMap(c => c.projects), 
                               ...projectsData.individualProjects].find(p => p.id === projectId);
                // console.log('project:', project);
                if (project?.collectionId) {
                    await ProjectService.removeProjectFromCollection(project.collectionId, projectId);
                };
                await ProjectService.deleteProject(projectId);
                
                
                const updatedProjects = projects.filter(project => project.id !== projectId);
                setProjects(updatedProjects);
            } catch (error) {
                console.error('Error deleting project:', error);
            }
        }
    };

    const handleDeleteCollection = async (collectionId) => {
        if (window.confirm('Are you sure you want to delete this collection and all its projects?')) {
            try {
                // Call the deleteCollection endpoint which now handles deleting all projects
                console.log('Deleting collection:', collectionId);
                await ProjectService.deleteCollection(collectionId);
                // Refresh the projects list
                setProjects([]); // Trigger re-fetch
                // Remove the collection from the state
                setProjectsData(prev => ({
                    ...prev,
                    collections: prev.collections.filter(c => c.id !== collectionId)
                }));
            } catch (error) {
                console.error('Error deleting collection:', error);
            }
        }
    };

    const handleEditName = project => {
        setEditingId(project.id);
        setEditName(project.name);
    };

    const handleSaveName = projectId => {
        if (editName.trim() !== '') {
            async function fetchUpdateProject() {
                const updatedProject = await ProjectService.updateProjectName(projectId, editName);
                const updatedProjects = projects.map(p => (p.id === projectId ? updatedProject : p));
                // Re-sort after updating
                const sortedProjects = updatedProjects.sort((a, b) => parseInt(b.createdAt) - parseInt(a.createdAt));
                setProjects(sortedProjects);
                setEditingId(null);
            }
            fetchUpdateProject();
        }
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditName('');
    };

    const toggleCollection = (collectionId) => {
        setExpandedCollections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(collectionId)) {
                newSet.delete(collectionId);
            } else {
                newSet.add(collectionId);
            }
            return newSet;
        });
    };

    return (
        <div className="min-h-screen bg-gray-100 p-6">
            <ProjectHeader showHomeButton={false} />
    
            <main className="pt-14 px-4 text-center">
                <h2 className="text-2xl font-semibold mb-4">Your Design Projects</h2>
    
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-blue-500 mb-4" />
                        <p className="text-gray-600">Loading your projects...</p>
                    </div>
                ) : projectsData.collections.length === 0 && projectsData.individualProjects.length === 0 ? (
                    <p className="mb-8 text-gray-600">
                        You don't have any projects yet. Redirecting to create a new project...
                    </p>
                ) : (
                    // This is where we call the renderProjects function
                    renderProjects()
                )}
    
                <Button
                    onClick={handleStartNewProject}
                    size="lg"
                    className="bg-blue-900 hover:bg-blue-800 text-white px-6 py-3"
                >
                    Start New Project
                </Button>
            </main>
        </div>
    );
};

export default UserProjects;